import { supabase } from '@/lib/supabase'

export type EvolucaoItem = {
  id: string
  title: string
  html: string
  created_at: string
  author?: string | null
  effective_at?: string | null
  updated_at?: string | null
  status?: 'pending' | 'published' | 'disabled'
  publish_at?: string | null
}

const APP_SETTINGS_KEY = 'evolucoes'
const LS_KEY = 'evolucoes.dev'

export async function listEvolucoes(opts?: { includePending?: boolean }): Promise<EvolucaoItem[]> {
  const includePending = !!opts?.includePending
  // 1) Preferred: RPC (if available in DB)
  try {
    const fn = includePending ? 'get_evolucoes_all' : 'get_evolucoes'
    const { data, error } = await supabase.rpc(fn as any)
    if (!error && Array.isArray(data) && data.length > 0) {
      const arr = normalize(data as any)
      return includePending ? arr : arr.filter((x) => (x.status ?? 'published') === 'published')
    }
  } catch {}

  // 2) Fallback: app_settings table (JSON array under key)
  try {
    const { data, error } = await supabase.from('app_settings').select('value').eq('key', APP_SETTINGS_KEY).maybeSingle()
    if (!error) {
      const raw = (data as any)?.value
      if (!raw) return []
      try {
        const arr = typeof raw === 'string' ? JSON.parse(raw) : raw
        if (Array.isArray(arr)) {
          let norm = normalize(arr)
          norm = await autoPromoteDue(norm)
          return includePending ? norm : norm.filter((x) => (x.status ?? 'published') === 'published')
        }
      } catch {}
    }
  } catch {}

  // 3) Dev fallback: localStorage (browser only)
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null
    if (raw) {
      let norm = normalize(JSON.parse(raw))
      norm = await autoPromoteDue(norm)
      return norm
    }
  } catch {}
  return []
}

export async function addEvolucao(item: { title: string; html: string; author?: string | null; effective_at?: string | null; publish_at?: string | null; status?: 'pending' | 'published' }): Promise<void> {
  const entry: EvolucaoItem = {
    id: crypto.randomUUID(),
    title: item.title,
    html: item.html,
    author: item.author || null,
    created_at: new Date().toISOString(),
    effective_at: item.effective_at || null,
    status: item.status || 'pending',
    publish_at: item.publish_at || null,
  }

  // 1) Preferred: RPC
  try {
    const payload: any = { p_title: entry.title, p_html: entry.html, p_author: entry.author }
    if (entry.effective_at != null) payload.p_effective_at = entry.effective_at
    if (entry.status != null) payload.p_status = entry.status
    if (entry.publish_at != null) payload.p_publish_at = entry.publish_at
    const { error } = await supabase.rpc('add_evolucao' as any, payload)
    if (!error) return
  } catch {}

  // 2) Fallback: app_settings
  try {
    const cur = await listEvolucoes({ includePending: true })
    const next = [entry, ...cur].sort((a, b) => (getSortDate(a) < getSortDate(b) ? 1 : -1))
    const { error } = await supabase
      .from('app_settings')
      .upsert({ key: APP_SETTINGS_KEY, value: JSON.stringify(next) }, { onConflict: 'key' })
    if (!error) return
  } catch {}

  // 3) Dev fallback: localStorage
  try {
    const curRaw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null
    const cur = curRaw ? (JSON.parse(curRaw) as EvolucaoItem[]) : []
    const next = [entry, ...cur]
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_KEY, JSON.stringify(next))
  } catch {}
}

export async function updateEvolucao(id: string, patch: Partial<Pick<EvolucaoItem, 'title' | 'html' | 'effective_at' | 'publish_at'>>): Promise<void> {
  // 1) Preferred: RPC
  try {
    const payload: any = { p_id: id }
    if (typeof patch.title === 'string') payload.p_title = patch.title
    if (typeof patch.html === 'string') payload.p_html = patch.html
    if (typeof patch.effective_at === 'string' || patch.effective_at === null) payload.p_effective_at = patch.effective_at
    if (typeof patch.publish_at === 'string' || patch.publish_at === null) payload.p_publish_at = patch.publish_at
    const { error } = await supabase.rpc('update_evolucao', payload)
    if (!error) return
  } catch {}

  // 1.5) Tabela direta (quando RPC não existir)
  try {
    const payload: any = {}
    if (typeof patch.title === 'string') payload.title = patch.title
    if (typeof patch.html === 'string') payload.html = patch.html
    if (typeof patch.effective_at === 'string' || patch.effective_at === null) payload.effective_at = patch.effective_at
    if (typeof patch.publish_at === 'string' || patch.publish_at === null) payload.publish_at = patch.publish_at
    payload.updated_at = new Date().toISOString()
    if (Object.keys(payload).length > 0) {
      const { error } = await supabase.from('evolucoes').update(payload).eq('id', id)
      if (!error) return
    }
  } catch {}

  // 2) Fallback: app_settings
  try {
    const cur = await listEvolucoes({ includePending: true })
    const nowIso = new Date().toISOString()
    const next = cur.map((it) => (it.id === id ? { ...it, ...patch, updated_at: nowIso } : it))
    const sorted = next.sort((a, b) => (getSortDate(a) < getSortDate(b) ? 1 : -1))
    const { error } = await supabase
      .from('app_settings')
      .upsert({ key: APP_SETTINGS_KEY, value: JSON.stringify(sorted) }, { onConflict: 'key' })
    if (!error) return
  } catch {}

  // 3) Dev fallback: localStorage
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null
    const cur = raw ? (JSON.parse(raw) as EvolucaoItem[]) : []
    const nowIso = new Date().toISOString()
    const next = cur.map((it) => (it.id === id ? { ...it, ...patch, updated_at: nowIso } : it))
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_KEY, JSON.stringify(next))
  } catch {}
}

export async function deleteEvolucao(id: string): Promise<void> {
  // 1) Preferred: RPC
  try {
    const { error } = await supabase.rpc('delete_evolucao', { p_id: id })
    if (!error) return
  } catch {}

  // 1.5) Tabela direta
  try {
    const { error } = await supabase.from('evolucoes').delete().eq('id', id)
    if (!error) return
  } catch {}

  // 2) Fallback: app_settings
  try {
    const cur = await listEvolucoes({ includePending: true })
    const next = cur.filter((it) => it.id !== id)
    const { error } = await supabase
      .from('app_settings')
      .upsert({ key: APP_SETTINGS_KEY, value: JSON.stringify(next) }, { onConflict: 'key' })
    if (!error) return
  } catch {}

  // 3) Dev fallback: localStorage
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null
    const cur = raw ? (JSON.parse(raw) as EvolucaoItem[]) : []
    const next = cur.filter((it) => it.id !== id)
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_KEY, JSON.stringify(next))
  } catch {}
}

export async function publishEvolucao(id: string): Promise<void> {
  // 1) Preferred: RPC
  try {
    const { error } = await supabase.rpc('publish_evolucao', { p_id: id })
    if (!error) return
  } catch {}

  // 1.5) Tabela direta
  try {
    const { error } = await supabase.from('evolucoes').update({ status: 'published', publish_at: null, updated_at: new Date().toISOString() }).eq('id', id)
    if (!error) return
  } catch {}

  // 2) Fallback: app_settings
  try {
    const cur = await listEvolucoes({ includePending: true })
    const nowIso = new Date().toISOString()
    const next = cur.map((it) => (it.id === id ? { ...it, status: 'published' as const, publish_at: null, updated_at: nowIso } : it))
    const sorted = next.sort((a, b) => (getSortDate(a) < getSortDate(b) ? 1 : -1))
    const { error } = await supabase
      .from('app_settings')
      .upsert({ key: APP_SETTINGS_KEY, value: JSON.stringify(sorted) }, { onConflict: 'key' })
    if (!error) return
  } catch {}

  // 3) Dev fallback: localStorage
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null
    const cur = raw ? (JSON.parse(raw) as EvolucaoItem[]) : []
    const nowIso = new Date().toISOString()
    const next = cur.map((it) => (it.id === id ? { ...it, status: 'published' as const, publish_at: null, updated_at: nowIso } : it))
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_KEY, JSON.stringify(next))
  } catch {}
}

export async function disableEvolucao(id: string): Promise<void> {
  // 1) Preferred: RPC
  try {
    const { error } = await supabase.rpc('disable_evolucao', { p_id: id })
    if (!error) return
  } catch {}

  // 1.5) Tabela direta
  try {
    const { error } = await supabase.from('evolucoes').update({ status: 'disabled', publish_at: null, updated_at: new Date().toISOString() }).eq('id', id)
    if (!error) return
  } catch {}

  // 2) Fallback: app_settings
  try {
    const cur = await listEvolucoes({ includePending: true })
    const nowIso = new Date().toISOString()
    const next = cur.map((it) => (it.id === id ? { ...it, status: 'disabled' as const, publish_at: null, updated_at: nowIso } : it))
    const sorted = next.sort((a, b) => (getSortDate(a) < getSortDate(b) ? 1 : -1))
    const { error } = await supabase
      .from('app_settings')
      .upsert({ key: APP_SETTINGS_KEY, value: JSON.stringify(sorted) }, { onConflict: 'key' })
    if (!error) return
  } catch {}

  // 3) Dev fallback: localStorage
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null
    const cur = raw ? (JSON.parse(raw) as EvolucaoItem[]) : []
    const nowIso = new Date().toISOString()
    const next = cur.map((it) => (it.id === id ? { ...it, status: 'disabled' as const, publish_at: null, updated_at: nowIso } : it))
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_KEY, JSON.stringify(next))
  } catch {}
}

function normalize(rows: any[]): EvolucaoItem[] {
  return (rows || [])
    .map((r) => ({
      id: String(r.id || r.uuid || crypto.randomUUID()),
      title: String(r.title || r.titulo || ''),
      html: String(r.html || r.content_html || r.content || ''),
      author: r.author || r.created_by || null,
      created_at: String(r.created_at || r.createdAt || new Date().toISOString()),
      effective_at: r.effective_at ? String(r.effective_at) : r.when ? String(r.when) : r.date ? String(r.date) : null,
      updated_at: r.updated_at ? String(r.updated_at) : null,
      status: (r.status === 'pending' || r.status === 'published' || r.status === 'disabled')
        ? r.status
        : (typeof r.is_published === 'boolean' ? (r.is_published ? 'published' : 'pending') : 'published'),
      publish_at: r.publish_at ? String(r.publish_at) : r.schedule_at ? String(r.schedule_at) : null,
    }))
    .sort((a, b) => (getSortDate(a) < getSortDate(b) ? 1 : -1))
}

function getSortDate(it: Pick<EvolucaoItem, 'created_at' | 'effective_at'>): string {
  return (it.effective_at || it.created_at || '')
}

async function autoPromoteDue(list: EvolucaoItem[]): Promise<EvolucaoItem[]> {
  try {
    const now = Date.now()
    let changed = false
    const next = list.map((it) => {
      if ((it.status ?? 'published') === 'pending' && it.publish_at) {
        const due = Date.parse(it.publish_at)
        if (!isNaN(due) && due <= now) {
          changed = true
          return { ...it, status: 'published' as const, publish_at: null, updated_at: new Date().toISOString() }
        }
      }
      return it
    })
    if (changed) {
      // persist back in app_settings (fallback only)
      await supabase
        .from('app_settings')
        .upsert({ key: APP_SETTINGS_KEY, value: JSON.stringify(next.sort((a, b) => (getSortDate(a) < getSortDate(b) ? 1 : -1))) }, { onConflict: 'key' })
    }
    return next
  } catch {
    return list
  }
}
